import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceArea,
} from "recharts";
import { getZoneThresholds, ZONE_DEFS } from "../../features/trainingSummary/zoneUtils";
import {
  chartDataFromSamples,
  MIN_SAMPLES_FOR_CHART,
} from "../../utils/liveHrHistoryBuffer";

export function LiveHrTrendChart({
  samples = [],
  mhr = 180,
  height = 120,
  compact = false,
  arena = false,
  fill = false,
  stale = false,
  collecting = false,
  ariaLabel = "",
}) {
  const wrapRef = useRef(null);
  const [fillHeight, setFillHeight] = useState(0);
  const data = useMemo(() => chartDataFromSamples(samples), [samples]);
  const thresholds = getZoneThresholds(mhr);
  const yMin = Math.round(mhr * 0.45);
  const yMax = mhr;
  const hasEnough = data.length >= MIN_SAMPLES_FOR_CHART;

  useEffect(() => {
    if (!fill || !wrapRef.current) return undefined;
    const el = wrapRef.current;
    const update = () => {
      setFillHeight(Math.max(arena ? 100 : 80, Math.floor(el.clientHeight)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fill, arena]);

  const resolvedHeight = fill ? fillHeight || height : height;
  const lineWidth = arena ? 3.5 : compact ? 2 : 3;
  const zoneOpacity = arena ? 0.38 : compact ? 0.35 : 0.5;

  return (
    <div
      ref={wrapRef}
      className={`lt-hr-trend-chart${compact ? " lt-hr-trend-chart--compact" : ""}${
        arena ? " lt-hr-trend-chart--arena" : ""
      }${fill ? " lt-hr-trend-chart--fill" : ""}${
        stale ? " lt-hr-trend-chart--stale" : ""
      }`}
      style={
        fill
          ? { width: "100%" }
          : { height, minHeight: height, width: "100%" }
      }
      role="img"
      aria-label={ariaLabel}
    >
      {(!fill || fillHeight > 0) ? (
      <ResponsiveContainer width="100%" height={resolvedHeight}>
        <LineChart
          data={data}
          margin={
            arena
              ? { top: 2, right: 2, left: 0, bottom: 0 }
              : { top: 4, right: 4, left: 0, bottom: 0 }
          }
        >
          <CartesianGrid
            stroke="rgba(255,255,255,0.08)"
            vertical={false}
          />
          {ZONE_DEFS.map(({ zone, bandColor }) => {
            const [lo, hi] = thresholds[zone];
            return (
              <ReferenceArea
                key={zone}
                y1={lo}
                y2={hi}
                fill={bandColor}
                fillOpacity={zoneOpacity}
                stroke="none"
                ifOverflow="hidden"
              />
            );
          })}
          {!compact && !arena ? (
            <XAxis
              dataKey="offsetSec"
              tick={{ fill: "rgba(248,250,252,0.45)", fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.12)" }}
              interval="preserveStartEnd"
              tickFormatter={(v) => `${v}s`}
            />
          ) : arena ? (
            <XAxis hide dataKey="offsetSec" />
          ) : null}
          {!compact ? (
            <YAxis
              domain={[yMin, yMax]}
              width={arena ? 22 : 28}
              tick={{ fill: "rgba(248,250,252,0.45)", fontSize: arena ? 9 : 10 }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
          ) : (
            <YAxis hide domain={[yMin, yMax]} />
          )}
          <Line
            type="monotone"
            dataKey="bpm"
            stroke={stale ? "rgba(148,163,184,0.7)" : "#ef4444"}
            strokeWidth={lineWidth}
            dot={false}
            isAnimationActive={false}
            strokeDasharray={collecting || !hasEnough ? "4 4" : undefined}
          />
        </LineChart>
      </ResponsiveContainer>
      ) : null}
    </div>
  );
}

export default LiveHrTrendChart;
